// Duplicate-read guard for barcode scanners.
//
// A handheld scanner sometimes fires twice on one pull — the two reads arrive
// 50–300 ms apart with the identical code. Fernando (2026-09-06): at the end of
// packing the second read of the SHIPPING LABEL landed in the next step, "scan
// the delivery note", and went to the server as an order number ("Not Found").
// The sibling nobody had reported yet: a double read of an ITEM counted it twice,
// because PackingWindow counts per scan (the backend /api/scan is stateless by
// design — see picking-packing-flow notes).
//
// One rule for every scan dispatcher: the same code again within
// DUPLICATE_SCAN_MS is the scanner, not the person — a packer cannot re-aim and
// re-trigger in 0.6 s, so two identical items scanned one after the other still
// both count. A code constant, not a setting: it describes the hardware, and
// tuning it is a reading-this-file decision (same reasoning as the pre-buy
// budget).
export const DUPLICATE_SCAN_MS = 600;

export type ScanGuard = (code: string) => boolean;

/** Returns a function that answers "is this a duplicate read?" for one screen.
 *  Keep one instance per component (in a ref); it remembers only the last code. */
export function makeScanGuard(windowMs: number = DUPLICATE_SCAN_MS): ScanGuard {
  let last = '';
  let at = 0;
  return (code: string) => {
    const c = (code || '').trim();
    const now = Date.now();
    const dup = c !== '' && c === last && now - at < windowMs;
    last = c;
    at = now;
    return dup;
  };
}
